(function () {
  const CONFIG_SELECTOR = 'script[type="application/json"][id^="pb-gallery-config-"]';
  const PRODUCT_CACHE = new Map();
  const INSTANCES = new Map();

  let observer = null;
  let isMounting = false;
  let remountTimer = null;

  const DEFAULTS = {
    gallery_selector: '.product__media-wrapper',
    product_details_selector: '.product-details',
    hide_original: true,
    layout: 'thumbnails_bottom',
    aspect_ratio: 'landscape',
    custom_aspect_ratio: '4 / 3',
    main_fit: 'contain',
    thumb_fit: 'cover',
    thumbs_per_row: 6,
    thumb_size: 96,
    thumb_align: 'center',
    thumb_gap: 4,
    image_gap: 4,
    show_counter: false,
    active_border_color: '#000000',
    inactive_border_color: '#ffffff',
    border_width: 1,
    border_radius: 0,
    circular_images: false,
    slide_transition: 'slide',
    zoom_type: 'lightbox',
    lightbox_zoom: '2',
    lightbox_bg_color: '#ffffff',
    initial_image: 'product_featured',
    variant_behavior: 'jump_to_variant_image',
    hide_arrows_with_media: false,
    link_variant_image_to_option: false,
    carousel: true,
    keyboard_navigation: false,
    preload_next_image: true,
    infinite_scroll: false,
    simulate_touch: false,
    video_native_player: false,
    video_autoplay: false,
    video_muted: false,
    video_controls: true,
    video_loop: false,
    external_video_autoplay: true,
    external_video_muted: false,
    external_video_controls: true,
    external_video_loop: true,
    model_auto_rotate: true,
    book_shadow: false,
    book_pages: 0,
  };

  ready(() => {
    patchHistory();
    mountAll();
    setTimeout(patchProductPageVariantMethod, 0);

    observer = new MutationObserver((mutations) => {
      if (mutations.every(isInternalGalleryMutation)) return;
      if (mutations.some(hasGalleryConfigMutation)) {
        scheduleMount();
        return;
      }
      patchProductPageVariantMethod();
      hideOriginalTargetsForAllInstances();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('change', handlePossibleVariantChange, true);
    document.addEventListener('variant:changed', handleVariantEvent, true);
    document.addEventListener('variant:change', handleVariantEvent, true);
    document.addEventListener('product:variant-change', handleVariantEvent, true);
    window.addEventListener('popstate', handlePossibleVariantChange);
    window.addEventListener('pb-gallery:refresh', () => scheduleMount());
  });

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function scheduleMount() {
    if (isMounting) return;
    clearTimeout(remountTimer);
    // Solo ocultar y bloquear instancias YA montadas (no nuevas configs)
    hideOriginalTargetsForAllInstances();
    for (const [, instance] of INSTANCES.entries()) {
      instance.roots.forEach((r) => {
        if (r.root.isConnected && r.root.offsetHeight > 0) {
          r.root.style.minHeight = `${r.root.offsetHeight}px`;
        }
      });
    }
    const delay = window.Shopify?.designMode ? 300 : 20;
    remountTimer = setTimeout(async () => {
      await mountAll();
      for (const [, instance] of INSTANCES.entries()) {
        instance.roots.forEach((r) => { r.root.style.minHeight = ''; });
      }
    }, delay);
  }

  async function mountAll() {
    if (isMounting) return;
    isMounting = true;

    try {
      const configs = getConfigs();
      for (const config of configs) {
        await mountGallery(config);
      }
      patchProductPageVariantMethod();
    } finally {
      isMounting = false;
    }
  }

  function getConfigs() {
    return Array.from(document.querySelectorAll(CONFIG_SELECTOR))
      .map((node) => {
        try {
          const parsed = JSON.parse(node.textContent || '{}');
          parsed.settings = { ...DEFAULTS, ...(parsed.settings || {}) };
          return parsed;
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  }

  async function mountGallery(config) {
    const blockId = config.blockId || 'default';
    const settings = config.settings;

    const product = normalizeProduct(config.product) || await fetchProduct(config.productHandle);
    const media = normalizeMedia(product);

    if (!product || media.length === 0) return;
    preloadGalleryImages(media);

    const targets = findTargets(settings);
    if (!targets.desktopTarget && !targets.productDetails) return;

    const existing = INSTANCES.get(blockId);
    const sameProduct = existing &&
      (existing.product.handle === (product.handle || config.productHandle));
    if (existing && sameProduct && existing.roots.some((r) => r.root.isConnected)) {
      if (settings.hide_original) hideOriginalTargets(settings, targets);
      const currentVariantId = getCurrentVariantId(config);
      if (currentVariantId && String(currentVariantId) !== String(existing.lastVariantId)) {
        existing.lastVariantId = String(currentVariantId);
        updateForVariant(existing.state, existing.product, existing.settings, currentVariantId);
      } else {
        existing.roots.forEach((r) => r.update());
      }
      return;
    }

    cleanup(blockId);

    const state = createState(product, media, config, settings);
    const desktop = createGalleryRoot(state, settings, 'desktop', blockId);
    const mobile = createGalleryRoot(state, settings, 'mobile', blockId);

    if (targets.desktopTarget) {
      // Insertar ANTES del target (como hermano) para no heredar sus
      // restricciones de overflow/height. Copiamos las propiedades de
      // grid/flex del target para ocupar la misma posición en el layout.
      const cs = getComputedStyle(targets.desktopTarget);
      ['gridArea','gridColumn','gridRow','alignSelf','justifySelf','order'].forEach((p) => {
        const v = cs[p];
        if (v && v !== 'auto' && v !== '0') desktop.root.style[p] = v;
      });
      targets.desktopTarget.parentElement.insertBefore(desktop.root, targets.desktopTarget);
      if (settings.hide_original) hideOriginalTargets(settings, targets);
    }

    if (targets.productDetails) {
      targets.productDetails.insertBefore(mobile.root, targets.productDetails.firstChild);
    }

    INSTANCES.set(blockId, {
      state,
      settings,
      product,
      roots: [desktop, mobile],
      lastVariantId: getCurrentVariantId(config),
    });

    desktop.update();
    mobile.update();
  }

  function cleanup(blockId) {
    document.querySelectorAll(`[data-pb-gallery-block="${cssEscape(blockId)}"]`).forEach((node) => {
      node.remove();
    });
  }

  function findExistingRoots(blockId) {
    return Array.from(document.querySelectorAll(`[data-pb-gallery-block="${cssEscape(blockId)}"]`));
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);

    const url = `/products/${encodeURIComponent(handle)}.js`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;

    const product = await response.json();
    PRODUCT_CACHE.set(handle, product);
    return product;
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;
    return {
      ...product,
      media: Array.isArray(product.media) ? product.media : [],
      variants: Array.isArray(product.variants) ? product.variants : [],
    };
  }

  function normalizeMedia(product) {
    const result = [];
    const seen = new Set();

    const media = Array.isArray(product?.media) ? product.media : [];
    for (const item of media) {
      const mediaType = item.media_type || 'image';
      if (!['image', 'video', 'external_video'].includes(mediaType)) continue;
      const src = normalizeSrc(item.src || item.preview_image?.src);
      if (!src || seen.has(src)) continue;
      seen.add(src);
      result.push({
        id: String(item.id || result.length + 1),
        mediaType,
        src,
        alt: item.alt || product.title || '',
        position: item.position || result.length + 1,
        filename: filenameFromSrc(src),
        variantIds: Array.isArray(item.variant_ids) ? item.variant_ids.map(String) : [],
        previewSrc: normalizeSrc(item.preview_image?.src || src),
        sources: Array.isArray(item.sources) ? item.sources : [],
        embedUrl: item.embed_url || item.embedded_url || externalVideoEmbedFromMedia(item),
        aspectRatio: mediaAspectRatio(item),
      });
    }

    if (result.length === 0 && Array.isArray(product?.images)) {
      product.images.forEach((src, index) => {
        const normalized = normalizeSrc(src);
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        result.push({
          id: String(index + 1),
          mediaType: 'image',
          src: normalized,
          alt: product.title || '',
          position: index + 1,
          filename: filenameFromSrc(normalized),
          variantIds: [],
          previewSrc: normalized,
          sources: [],
          embedUrl: '',
          aspectRatio: '',
        });
      });
    }

    linkVariantsToMedia(result, product?.variants || []);
    return result;
  }

  function linkVariantsToMedia(media, variants) {
    variants.forEach((variant) => {
      const variantId = variant?.id ? String(variant.id) : '';
      if (!variantId) return;
      const matched = findMediaForVariant(media, variant);
      if (!matched) return;
      if (!matched.variantIds.includes(variantId)) {
        matched.variantIds.push(variantId);
      }
    });
  }

  function createState(product, media, config, settings) {
    const selectedVariant = findVariant(product, getCurrentVariantId(config));
    const initialSrc =
      settings.initial_image === 'selected_variant'
        ? normalizeSrc(selectedVariant?.featured_image?.src || selectedVariant?.featured_media?.preview_image?.src)
        : normalizeSrc(product.featured_image);

    const activeIndex = findMediaIndex(media, initialSrc);

    return {
      product,
      allMedia: media,
      visibleMedia: media.slice(),
      activeIndex: activeIndex >= 0 ? activeIndex : 0,
      roots: [],
      setActive(index, options = {}) {
        this.activeIndex = clamp(index, 0, this.visibleMedia.length - 1);
        this.roots.forEach((root) => root.update());
        if (options.syncVariant && settings.link_variant_image_to_option) {
          syncVariantFromMedia(this.visibleMedia[this.activeIndex], product);
        }
      },
      setVisible(nextVisible, preferredSrc) {
        this.visibleMedia = nextVisible.length ? nextVisible : this.allMedia.slice();
        const preferredIndex = findMediaIndex(this.visibleMedia, preferredSrc);
        this.activeIndex = preferredIndex >= 0 ? preferredIndex : 0;
        this.roots.forEach((root) => root.update());
      },
      next() {
        this.setActive((this.activeIndex + 1) % this.visibleMedia.length, { syncVariant: true });
      },
      prev() {
        this.setActive((this.activeIndex - 1 + this.visibleMedia.length) % this.visibleMedia.length, { syncVariant: true });
      },
    };
  }

  function createGalleryRoot(state, settings, mode, blockId) {
    const root = document.createElement('div');
    root.className = [
      'pb-gallery',
      `pb-gallery--${mode}`,
      `pb-gallery--layout-${settings.layout}`,
      `pb-gallery--transition-${settings.slide_transition || 'slide'}`,
      settings.circular_images ? 'pb-gallery--circular' : '',
      settings.main_fit === 'contain' ? 'pb-gallery--main-contain' : '',
    ]
      .filter(Boolean)
      .join(' ');
    root.dataset.pbGalleryBlock = blockId;
    root.dataset.pbGalleryMode = mode;
    setGalleryVariables(root, settings);
    if (settings.book_shadow) {
      root.classList.add('pb-gallery--book-shadow');
      const pages = Number(settings.book_pages) || 0;
      const spine = pages > 0 ? Math.min(Math.max(Math.round(pages * 0.1), 6), 50) : 14;
      root.style.setProperty('--pb-book-spine', `${spine}px`);
    }

    const main = document.createElement('div');
    main.className = 'pb-gallery__main';
    main.tabIndex = 0;

    const mediaFrame = document.createElement('div');
    mediaFrame.className = 'pb-gallery__main-frame';
    main.appendChild(mediaFrame);

    const imageLayer = document.createElement('div');
    imageLayer.className = 'pb-gallery__image-layer';
    const dynamicFrame = document.createElement('div');
    dynamicFrame.className = 'pb-gallery__dynamic-frame';
    mediaFrame.append(imageLayer, dynamicFrame);

    const imageNodes = new Map();
    const mainWidths = mode === 'mobile' ? [600, 900, 1200] : [800, 1200, 1600, 2000];
    const mainSizes = mode === 'mobile'
      ? '100vw'
      : '(min-width: 1200px) 700px, (min-width: 768px) 50vw, 100vw';
    state.allMedia.forEach((item, index) => {
      if (item.mediaType !== 'image') return;
      const isFirst = index === 0;
      const img = document.createElement('img');
      img.className = 'pb-gallery__main-img pb-gallery__main-img-layer';
      img.loading = isFirst ? 'eager' : 'lazy';
      img.decoding = 'async';
      if (isFirst) img.setAttribute('fetchpriority', 'high');
      img.src = sizedImage(item.src, mode === 'mobile' ? 1200 : 1600);
      img.srcset = srcsetFor(item.src, mainWidths);
      img.sizes = mainSizes;
      img.alt = item.alt || state.product.title || '';
      img.setAttribute('aria-hidden', 'true');
      const dims = imgDimensions(item);
      if (dims.width) { img.width = dims.width; img.height = dims.height; }
      imageLayer.appendChild(img);
      imageNodes.set(item.id, img);
    });

    const count = document.createElement('span');
    count.className = 'pb-gallery__count';
    main.appendChild(count);

    const thumbs = document.createElement('div');
    thumbs.className = 'pb-gallery__thumbs';

    root.appendChild(main);
    root.appendChild(thumbs);

    let thumbsSignature = '';
    let activeMediaId = null;

    const instance = {
      root,
      update() {
        const active = state.visibleMedia[state.activeIndex] || state.visibleMedia[0];
        if (!active) return;

        const singleMode = state.visibleMedia.length === 1;
        const imgRatio = singleMode ? singleAspectRatio(settings) : 'var(--pb-ratio)';
        main.style.setProperty('--pb-current-ratio', active.mediaType === 'image' ? imgRatio : mediaRatio(active));
        main.classList.toggle('pb-gallery__main--image', active.mediaType === 'image');
        main.classList.toggle('pb-gallery__main--video', active.mediaType !== 'image');
        main.tabIndex = active.mediaType === 'image' ? 0 : -1;
        main.setAttribute('role', active.mediaType === 'image' ? 'button' : 'group');
        if (active.mediaType === 'image') {
          if (activeMediaId !== active.id) dynamicFrame.replaceChildren();
          imageNodes.forEach((node, id) => {
            const isActive = id === active.id;
            node.classList.toggle('is-active', isActive);
            node.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
          activeMediaId = active.id;
        } else if (activeMediaId !== active.id) {
          imageNodes.forEach((node) => {
            node.classList.remove('is-active');
            node.setAttribute('aria-hidden', 'true');
          });
          if (root.offsetParent !== null) {
            renderMainMedia(dynamicFrame, active, state.product.title || '', mode, settings);
            activeMediaId = active.id;
          } else {
            dynamicFrame.replaceChildren();
          }
        }

        const hasMultipleMedia = state.visibleMedia.length > 1;
        count.hidden = !hasMultipleMedia || !settings.show_counter;
        thumbs.hidden = !hasMultipleMedia;
        root.classList.toggle('pb-gallery--single', !hasMultipleMedia);
        count.textContent = `${state.activeIndex + 1} / ${state.visibleMedia.length}`;
        if (settings.preload_next_image && hasMultipleMedia) {
          preloadMedia(state.visibleMedia[(state.activeIndex + 1) % state.visibleMedia.length]);
        }

        const nextSignature = state.visibleMedia.map((item) => item.id).join('|');
        if (nextSignature !== thumbsSignature) {
          thumbsSignature = nextSignature;
          thumbs.replaceChildren(
            ...state.visibleMedia.map((item, index) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'pb-gallery__thumb';
              const labelType = item.mediaType === 'image' ? 'Imagen' : 'Video';
              button.setAttribute('aria-label', item.alt || `${labelType} ${index + 1}`);
              button.addEventListener('click', () => state.setActive(index, { syncVariant: true }));

              const thumb = document.createElement('img');
              thumb.className = 'pb-gallery__thumb-img';
              const thumbSrc = item.previewSrc || item.src;
              thumb.src = sizedImage(thumbSrc, 260);
              thumb.srcset = srcsetFor(thumbSrc, [130, 260, 400]);
              thumb.sizes = `${settings.thumb_size || 96}px`;
              thumb.alt = item.alt || '';
              thumb.loading = index === 0 ? 'eager' : 'lazy';
              thumb.decoding = 'async';
              const tdims = imgDimensions(item);
              if (tdims.width) { thumb.width = tdims.width; thumb.height = tdims.height; }
              button.appendChild(thumb);
              if (item.mediaType !== 'image') {
                const icon = document.createElement('span');
                icon.className = 'pb-gallery__thumb-video-icon';
                icon.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><polygon points="6,4 20,12 6,20" fill="#fff"/></svg>';
                button.appendChild(icon);
              }

              return button;
            })
          );
        }
        Array.from(thumbs.children).forEach((button, index) => {
          button.setAttribute('aria-current', index === state.activeIndex ? 'true' : 'false');
        });
      },
    };

    main.addEventListener('click', () => {
      const active = state.visibleMedia[state.activeIndex] || state.visibleMedia[0];
      if (!active || active.mediaType !== 'image') return;
      if (settings.zoom_type === 'lightbox' || settings.zoom_type === 'lightbox_magnifier') {
        openLightbox(state, settings);
      }
    });
    main.addEventListener('keydown', (event) => {
      if (!['Enter', ' '].includes(event.key)) return;
      const active = state.visibleMedia[state.activeIndex] || state.visibleMedia[0];
      if (!active || active.mediaType !== 'image') return;
      event.preventDefault();
      if (settings.zoom_type === 'lightbox' || settings.zoom_type === 'lightbox_magnifier') {
        openLightbox(state, settings);
      }
    });

    if (settings.zoom_type === 'magnifier' || settings.zoom_type === 'lightbox_magnifier') {
      setupMagnifier(main, () => {
        const active = state.visibleMedia[state.activeIndex] || state.visibleMedia[0];
        return active && active.mediaType === 'image' ? active.src : '';
      });
    }

    if (settings.keyboard_navigation) {
      root.tabIndex = 0;
      root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') state.next();
        if (event.key === 'ArrowLeft') state.prev();
      });
    }

    state.roots.push(instance);
    return instance;
  }

  function setGalleryVariables(root, settings) {
    root.style.setProperty('--pb-active-border-color', settings.active_border_color || '#000');
    root.style.setProperty('--pb-inactive-border-color', settings.inactive_border_color || '#fff');
    root.style.setProperty('--pb-thumb-gap', `${Number(settings.thumb_gap || 0)}px`);
    root.style.setProperty('--pb-image-gap', `${Number(settings.image_gap || 0)}px`);
    root.style.setProperty('--pb-thumbs-per-row', String(settings.thumbs_per_row || 6));
    root.style.setProperty('--pb-thumb-size', `${Number(settings.thumb_size || 96)}px`);
    root.style.setProperty('--pb-thumb-align', thumbAlign(settings.thumb_align));
    root.style.setProperty('--pb-border-width', `${Number(settings.border_width || 0)}px`);
    root.style.setProperty('--pb-border-radius', `${Number(settings.border_radius || 0)}px`);
    root.style.setProperty('--pb-main-fit', settings.main_fit === 'cover' ? 'cover' : 'contain');
    root.style.setProperty('--pb-thumb-fit', settings.thumb_fit === 'contain' ? 'contain' : 'cover');
    root.style.setProperty('--pb-ratio', aspectRatio(settings));
  }

  function thumbAlign(value) {
    if (value === 'left') return 'flex-start';
    if (value === 'right') return 'flex-end';
    return 'center';
  }

  function aspectRatio(settings) {
    if (settings.aspect_ratio === 'square') return '1 / 1';
    if (settings.aspect_ratio === 'portrait') return '3 / 4';
    if (settings.aspect_ratio === 'natural') return 'auto';
    if (settings.aspect_ratio === 'custom' && settings.custom_aspect_ratio) {
      return settings.custom_aspect_ratio;
    }
    return '4 / 3';
  }

  function singleAspectRatio(settings) {
    const base = aspectRatio(settings);
    if (base === 'auto') return base;
    const m = base.match(/^([\d.]+)\s*\/\s*([\d.]+)$/);
    if (!m) return base;
    return `${m[1]} / ${(parseFloat(m[2]) * 1.22).toFixed(3)}`;
  }

  function mediaAspectRatio(media) {
    const numericRatio = Number(media?.aspect_ratio || media?.aspectRatio);
    if (Number.isFinite(numericRatio) && numericRatio > 0) {
      return String(numericRatio);
    }

    const width = Number(media?.width || media?.preview_image?.width);
    const height = Number(media?.height || media?.preview_image?.height);
    if (width > 0 && height > 0) {
      return `${width} / ${height}`;
    }

    return '';
  }

  function mediaRatio(media) {
    return media?.aspectRatio || '16 / 9';
  }

  function openLightbox(state, settings) {
    const overlay = document.createElement('div');
    overlay.className = 'pb-gallery__lightbox';
    overlay.style.setProperty('--pb-lightbox-bg', settings.lightbox_bg_color || '#fff');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const viewer = document.createElement('div');
    viewer.className = 'pb-gallery__lightbox-viewer';

    const img = document.createElement('img');
    img.className = 'pb-gallery__lightbox-img';
    viewer.appendChild(img);

    const navigator = document.createElement('div');
    navigator.className = 'pb-gallery__lightbox-navigator';
    const navigatorImg = document.createElement('img');
    navigatorImg.className = 'pb-gallery__lightbox-navigator-img';
    const navigatorFrame = document.createElement('span');
    navigatorFrame.className = 'pb-gallery__lightbox-navigator-frame';
    navigator.append(navigatorImg, navigatorFrame);

    const close = lightboxButton('pb-gallery__lightbox-close', 'x', 'Cerrar');
    const prev = lightboxButton('pb-gallery__lightbox-prev', '‹', 'Anterior');
    const next = lightboxButton('pb-gallery__lightbox-next', '›', 'Siguiente');
    const count = document.createElement('span');
    count.className = 'pb-gallery__lightbox-count';

    const zoomLevel = document.createElement('span');
    zoomLevel.className = 'pb-gallery__lightbox-zoom-level';

    overlay.append(viewer, navigator, close, prev, next, count, zoomLevel);

    let zoom = 1;
    const maxZoom = clamp(Number(settings.lightbox_zoom || 2), 1.25, 4);
    let pan = { x: 0, y: 0 };
    let drag = null;

    const clampPan = () => {
      const maxX = Math.max(0, (img.offsetWidth * zoom - viewer.offsetWidth) / 2);
      const maxY = Math.max(0, (img.offsetHeight * zoom - viewer.offsetHeight) / 2);
      pan.x = clamp(pan.x, -maxX, maxX);
      pan.y = clamp(pan.y, -maxY, maxY);
    };

    const applyTransform = () => {
      clampPan();
      img.style.transform = `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`;
      zoomLevel.textContent = `${zoom.toFixed(1).replace('.0', '')}x`;
      updateNavigator();
    };

    const resetView = () => {
      zoom = 1;
      pan = { x: 0, y: 0 };
      applyTransform();
    };

    const updateNavigator = () => {
      const imgW = img.offsetWidth * zoom;
      const imgH = img.offsetHeight * zoom;
      if (!imgW || !imgH || !navigatorImg.offsetWidth) return;

      const visibleW = clamp(viewer.offsetWidth / imgW, 0.08, 1);
      const visibleH = clamp(viewer.offsetHeight / imgH, 0.08, 1);
      const centerX = clamp(0.5 - pan.x / imgW, visibleW / 2, 1 - visibleW / 2);
      const centerY = clamp(0.5 - pan.y / imgH, visibleH / 2, 1 - visibleH / 2);

      navigatorFrame.style.width = `${visibleW * 100}%`;
      navigatorFrame.style.height = `${visibleH * 100}%`;
      navigatorFrame.style.left = `${(centerX - visibleW / 2) * 100}%`;
      navigatorFrame.style.top = `${(centerY - visibleH / 2) * 100}%`;
      navigator.hidden = zoom <= 1.05;
    };

    const render = () => {
      const active = state.visibleMedia[state.activeIndex] || state.visibleMedia[0];
      if (!active || active.mediaType !== 'image') return;
      img.src = sizedImage(active.src, 2400);
      img.alt = active.alt || '';
      navigatorImg.src = sizedImage(active.src, 360);
      navigatorImg.alt = '';
      count.textContent = `${state.activeIndex + 1} / ${state.visibleMedia.length}`;
      state.roots.forEach((root) => root.update());
      if (img.complete) resetView();
    };

    const destroy = () => {
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('resize', applyTransform);
      overlay.remove();
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') destroy();
      if (event.key === 'ArrowRight') {
        state.next();
        render();
      }
      if (event.key === 'ArrowLeft') {
        state.prev();
        render();
      }
    };

    close.addEventListener('click', destroy);
    prev.addEventListener('click', () => {
      state.prev();
      render();
    });
    next.addEventListener('click', () => {
      state.next();
      render();
    });
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) destroy();
    });
    img.addEventListener('load', resetView);
    viewer.addEventListener('dblclick', resetView);
    viewer.addEventListener('wheel', (event) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.25 : 0.25;
      const nextZoom = clamp(zoom + delta, 1, maxZoom);
      if (nextZoom === zoom) return;
      const rect = viewer.getBoundingClientRect();
      const cx = event.clientX - rect.left - rect.width / 2;
      const cy = event.clientY - rect.top - rect.height / 2;
      const ratio = nextZoom / zoom;
      pan.x = cx + (pan.x - cx) * ratio;
      pan.y = cy + (pan.y - cy) * ratio;
      zoom = nextZoom;
      applyTransform();
    }, { passive: false });
    viewer.addEventListener('pointerdown', (event) => {
      if (zoom <= 1.05) return;
      viewer.setPointerCapture(event.pointerId);
      drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
      viewer.classList.add('is-dragging');
    });
    viewer.addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      pan.x = drag.panX + event.clientX - drag.x;
      pan.y = drag.panY + event.clientY - drag.y;
      applyTransform();
    });
    viewer.addEventListener('pointerup', (event) => {
      if (drag?.pointerId === event.pointerId) drag = null;
      viewer.classList.remove('is-dragging');
    });
    navigator.addEventListener('click', (event) => {
      const rect = navigator.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      const imageRect = img.getBoundingClientRect();
      pan.x = (0.5 - x) * imageRect.width;
      pan.y = (0.5 - y) * imageRect.height;
      applyTransform();
    });
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', applyTransform);

    document.body.appendChild(overlay);
    render();
  }

  function lightboxButton(className, text, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pb-gallery__lightbox-button ${className}`;
    button.textContent = text;
    button.setAttribute('aria-label', label);
    return button;
  }

  function setupMagnifier(main, srcGetter) {
    main.classList.add('pb-gallery__main--magnifier');
    main.addEventListener('mousemove', (event) => {
      const src = srcGetter();
      if (!src) return;
      const rect = main.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      main.style.backgroundImage = `url("${sizedImage(src, 1800)}")`;
      main.style.backgroundSize = '220%';
      main.style.backgroundPosition = `${x}% ${y}%`;
      const image = main.querySelector('.pb-gallery__main-img');
      if (image) image.style.opacity = '0';
    });
    main.addEventListener('mouseleave', () => {
      main.style.backgroundImage = '';
      const image = main.querySelector('.pb-gallery__main-img');
      if (image) image.style.opacity = '';
    });
  }

  function renderMainMedia(container, media, fallbackTitle, mode, settings) {
    container.replaceChildren();
    if (media.mediaType === 'image') {
      const img = document.createElement('img');
      img.className = 'pb-gallery__main-img';
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = sizedImage(media.src, mode === 'mobile' ? 1200 : 1800);
      img.alt = media.alt || fallbackTitle || '';
      container.appendChild(img);
      return;
    }

    if (media.mediaType === 'video') {
      const video = document.createElement('video');
      video.className = 'pb-gallery__main-video';
      video.autoplay = Boolean(settings.video_autoplay);
      video.muted = Boolean(settings.video_muted || settings.video_autoplay);
      video.controls = settings.video_controls !== false;
      video.loop = Boolean(settings.video_loop);
      video.playsInline = true;
      video.preload = 'metadata';
      if (media.previewSrc) video.poster = sizedImage(media.previewSrc, 1200);
      const sources = Array.isArray(media.sources) ? media.sources : [];
      if (sources.length) {
        for (const sourceItem of sources) {
          const sourceUrl = normalizeSrc(sourceItem.url || sourceItem.src || '');
          if (!sourceUrl) continue;
          const source = document.createElement('source');
          source.src = sourceUrl;
          if (sourceItem.mime_type) source.type = sourceItem.mime_type;
          video.appendChild(source);
        }
      }
      if (!video.children.length && media.src && media.src !== media.previewSrc) {
        video.src = normalizeSrc(media.src);
      }
      container.appendChild(video);
      return;
    }

    if (media.mediaType === 'external_video') {
      const buildIframe = (autoplay) => {
        const iframe = document.createElement('iframe');
        iframe.className = 'pb-gallery__main-video pb-gallery__main-video--external';
        iframe.src = externalVideoUrl(media.embedUrl || '', { ...settings, external_video_autoplay: autoplay });
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = media.alt || fallbackTitle || 'Video';
        return iframe;
      };

      if (settings.external_video_autoplay) {
        container.appendChild(buildIframe(true));
        return;
      }

      const facade = document.createElement('div');
      facade.className = 'pb-gallery__video-facade';

      if (media.previewSrc) {
        const poster = document.createElement('img');
        poster.className = 'pb-gallery__video-facade-poster';
        poster.src = sizedImage(media.previewSrc, 1200);
        poster.alt = media.alt || fallbackTitle || '';
        poster.loading = 'eager';
        facade.appendChild(poster);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pb-gallery__video-facade-play';
      btn.setAttribute('aria-label', 'Reproducir video');
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48"><circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.55)"/><polygon points="9.5,7 18.5,12 9.5,17" fill="#fff"/></svg>';
      facade.appendChild(btn);

      btn.addEventListener('click', () => container.replaceChildren(buildIframe(true)));
      container.appendChild(facade);
      return;
    }

    const fallback = document.createElement('img');
    fallback.className = 'pb-gallery__main-img';
    fallback.src = sizedImage(media.src, mode === 'mobile' ? 1200 : 1800);
    fallback.alt = media.alt || fallbackTitle || '';
    container.appendChild(fallback);
  }

  function preloadMedia(media) {
    if (!media || media.mediaType !== 'image') return;
    const image = new Image();
    image.src = sizedImage(media.src, 1200);
  }

  function preloadGalleryImages(media) {
    media.forEach((item) => {
      if (item.mediaType !== 'image') return;
      [260, 1200, 1800].forEach((width) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = sizedImage(width === 260 ? item.previewSrc || item.src : item.src, width);
      });
    });
  }

  function externalVideoUrl(src, settings) {
    const normalized = normalizeSrc(src);
    if (!normalized) return '';
    try {
      const url = new URL(normalized, window.location.origin);
      if (settings.external_video_autoplay) url.searchParams.set('autoplay', '1');
      if (settings.external_video_muted) url.searchParams.set('mute', '1');
      if (settings.external_video_controls === false) url.searchParams.set('controls', '0');
      if (settings.external_video_loop) {
        url.searchParams.set('loop', '1');
        // YouTube requires playlist=VIDEO_ID for loop to work
        const videoId = url.pathname.split('/').filter(Boolean).pop();
        if (videoId && url.hostname.includes('youtube')) {
          url.searchParams.set('playlist', videoId);
        }
      }
      return url.toString();
    } catch {
      return normalized;
    }
  }

  function externalVideoEmbedFromMedia(item) {
    const externalId = item.external_id || item.externalId;
    if (!externalId) return '';
    if (item.host === 'youtube') return `https://www.youtube.com/embed/${externalId}`;
    if (item.host === 'vimeo') return `https://player.vimeo.com/video/${externalId}`;
    return '';
  }

  function findTargets(settings) {
    const gallerySelector = settings.gallery_selector || DEFAULTS.gallery_selector;
    const productDetailsSelector = settings.product_details_selector || DEFAULTS.product_details_selector;

    return {
      desktopTarget: document.querySelector(gallerySelector),
      productDetails: document.querySelector(productDetailsSelector),
    };
  }

  function hideOriginalTargets(settings, targets) {
    const gallerySelector = settings.gallery_selector || DEFAULTS.gallery_selector;

    document.querySelectorAll(gallerySelector).forEach((node) => {
      if (node.closest('.pb-gallery')) return;
      node.dataset.pbGalleryHidden = 'true';
      node.style.setProperty('display', 'none', 'important');
      node.setAttribute('aria-hidden', 'true');
    });

    const productDetails = targets.productDetails;
    const mobileImage = productDetails?.querySelector('[x-element-transition-dest="mobile-img"]');
    const mobileWrapper = mobileImage?.closest('.mb-4') || mobileImage?.parentElement;
    if (mobileWrapper && !mobileWrapper.classList.contains('pb-gallery')) {
      mobileWrapper.dataset.pbGalleryHidden = 'true';
      mobileWrapper.style.setProperty('display', 'none', 'important');
      mobileWrapper.setAttribute('aria-hidden', 'true');
    }

    const next = productDetails?.nextElementSibling;
    if (next && String(next.className).includes('md:hidden') && String(next.className).includes('-mt-8')) {
      next.dataset.pbGalleryHidden = 'true';
      next.style.setProperty('display', 'none', 'important');
      next.setAttribute('aria-hidden', 'true');
    }

    document
      .querySelectorAll('[x-element-transition-dest="mobile-img"], .product-details ~ [class*="-mt-8"][class*="md:hidden"]')
      .forEach((node) => {
        if (node.closest('.pb-gallery')) return;
        const wrapper = node.closest('.mb-4') || node;
        wrapper.dataset.pbGalleryHidden = 'true';
        wrapper.style.setProperty('display', 'none', 'important');
        wrapper.setAttribute('aria-hidden', 'true');
      });
  }

  function handlePossibleVariantChange(event) {
    const target = event?.target;
    if (target?.closest?.('.pb-gallery')) return;

    const isLikelyVariantEvent =
      !target ||
      target.matches?.('input[name="id"], select[name="id"], input[type="radio"], select') ||
      target.closest?.('[data-option-value], variant-selects, variant-radios, product-form');

    if (!isLikelyVariantEvent) return;

    setTimeout(() => {
      for (const [, instance] of INSTANCES.entries()) {
        const nextVariantId = getCurrentVariantId({ selectedVariantId: instance.lastVariantId });
        if (!nextVariantId || String(nextVariantId) === String(instance.lastVariantId)) continue;

        instance.lastVariantId = String(nextVariantId);
        updateForVariant(instance.state, instance.product, instance.settings, nextVariantId);
      }

    }, 120);
  }

  function hideOriginalTargetsForAllInstances() {
    for (const [, instance] of INSTANCES.entries()) {
      hideOriginalTargets(instance.settings, findTargets(instance.settings));
    }
  }

  function syncVariantFromMedia(media, product) {
    const variant = findVariantForMedia(product, media);
    if (!variant?.id) return;

    const variantId = String(variant.id);
    if (String(getCurrentVariantId()) === variantId) return;

    updateVariantFormValue(variantId);
    updateProductPageState(variant);
    updateVariantUrl(variantId);
    dispatchVariantChanged(variant);
  }

  function patchProductPageVariantMethod() {
    const data = findProductPageState();
    if (!data || data.__pbGalleryVariantPatched || typeof data.selectOptionAndSetVariantId !== 'function') return;

    data.__pbGalleryOriginalSelectOptionAndSetVariantId = data.selectOptionAndSetVariantId.bind(data);
    data.selectOptionAndSetVariantId = function pbGallerySelectOption(optionIndex, optionValue, variantId) {
      if (!this.selectedOptions) return;

      this.selectedOptions[optionIndex] = optionValue;
      this.isVariantManuallySelected = true;
      this.isVariantAvailable = Array.isArray(this.variantsOptions)
        ? this.variantsOptions.some((options) => options.every((option) => this.selectedOptions?.includes(option)))
        : true;

      let nextVariantId = variantId;
      if (!this.isVariantAvailable) {
        const value = Object.values(this.selectedOptions)[0];
        const fallbackVariant =
          this.variants.find((item) => item.option1 === value) ||
          this.variants.find((item) => item.option2 === value) ||
          this.variants.find((item) => item.option3 === value);
        if (fallbackVariant?.id) nextVariantId = fallbackVariant.id;
      }

      const variant = findVariant({ variants: this.variants }, nextVariantId);
      if (!variant?.id) return;

      this.selectedVariantId = Number.isFinite(Number(variant.id)) ? Number(variant.id) : variant.id;
      this.selectedOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
      this.isVariantAvailable = variant.available !== false;

      updateVariantFormValue(String(variant.id));
      updateVariantPrice(variant);
      if (typeof this._updateSelectedVariantCartState === 'function') {
        this._updateSelectedVariantCartState();
      }
      updateVariantUrl(String(variant.id));
      dispatchVariantChanged(variant);
    };
    data.__pbGalleryVariantPatched = true;
  }

  function updateVariantFormValue(variantId) {
    document.querySelectorAll('form[action*="/cart/add"] [name="id"], [name="id"]').forEach((field) => {
      if (field.tagName === 'SELECT') {
        field.value = variantId;
      } else if (field.type === 'radio') {
        field.checked = String(field.value) === variantId;
      } else {
        field.value = variantId;
      }
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function updateProductPageState(variant) {
    const data = findProductPageState();
    if (!data) return;

    data.selectedVariantId = Number.isFinite(Number(variant.id)) ? Number(variant.id) : variant.id;
    data.selectedOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
    data.isVariantManuallySelected = true;
    data.isVariantAvailable = variant.available !== false;
    updateVariantPrice(variant);
    if (typeof data._updateSelectedVariantCartState === 'function') {
      data._updateSelectedVariantCartState();
    }
  }

  function updateVariantPrice(variant) {
    if (variant?.price == null) return;

    document.querySelectorAll('[id^="price-"] .price__element').forEach((priceElement) => {
      const current = priceElement.querySelector('.text-primary-600');
      if (current) current.textContent = formatMoney(variant.price);

      const compare = priceElement.querySelector('s');
      if (!compare) return;
      if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
        compare.textContent = formatMoney(variant.compare_at_price);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    });
  }

  function formatMoney(value) {
    const mainStore = window.Alpine?.store?.('main');
    if (typeof mainStore?.getPrice === 'function') {
      return mainStore.getPrice(value);
    }
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(value);
    }
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
      style: 'currency',
      currency,
    }).format(Number(value) / 100);
  }

  function findProductPageState() {
    if (!window.Alpine?.$data) return null;
    const nodes = document.querySelectorAll('[x-data]');
    for (const node of nodes) {
      if (!String(node.getAttribute('x-data') || '').includes('ProductPage')) continue;
      const data = window.Alpine.$data(node);
      if (data && 'selectedVariantId' in data) return data;
    }
    return null;
  }

  function updateVariantUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState(history.state, '', url.href);
    } catch {
      // URL sync is non-critical.
    }
  }

  function dispatchVariantChanged(variant) {
    const detail = { variant };
    document.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail }));
  }

  function handleVariantEvent(event) {
    const variant = event?.detail?.variant;
    if (!variant) return;

    const variantId = variant.id ? String(variant.id) : null;
    const variantSrc = normalizeSrc(
      variant.featured_image?.src ||
        variant.featured_media?.preview_image?.src ||
        variant.featured_media?.src
    );

    for (const [, instance] of INSTANCES.entries()) {
      if (variantId) {
        instance.lastVariantId = variantId;
      }

      if (variantId) {
        updateForVariant(instance.state, instance.product, instance.settings, variantId);
        continue;
      }

      if (variantSrc) {
        instance.state.setVisible(instance.state.allMedia.slice(), variantSrc);
      }
    }

  }

  function isInternalGalleryMutation(mutation) {
    const nodes = [mutation.target, ...mutation.addedNodes, ...mutation.removedNodes].filter(Boolean);
    return nodes.length > 0 && nodes.every((node) => {
      if (node.nodeType !== 1) return true;
      return node.classList?.contains('pb-gallery') || node.closest?.('.pb-gallery');
    });
  }

  function hasGalleryConfigMutation(mutation) {
    const nodes = [...mutation.addedNodes].filter(Boolean);
    return nodes.some((node) => {
      if (node.nodeType !== 1) return false;
      return node.matches?.(CONFIG_SELECTOR) || node.querySelector?.(CONFIG_SELECTOR);
    });
  }

  function updateForVariant(state, product, settings, variantId) {
    const variant = findVariant(product, variantId);
    if (!variant) return;

    const variantMedia = findMediaForVariant(state.allMedia, variant);
    const variantSrc = variantMedia?.src || variantImageSrc(variant);
    const behavior = settings.variant_behavior;

    if (behavior === 'keep_all_images' || behavior === 'jump_to_variant_image') {
      state.setVisible(state.allMedia.slice(), variantSrc);
      return;
    }

    if (behavior === 'filter_by_variant_image') {
      const next = state.allMedia.filter((item) => {
        if (!variantSrc) return true;
        return item.variantIds.includes(String(variant.id)) || sameImage(item.src, variantSrc) || item.variantIds.length === 0;
      });
      state.setVisible(next, variantSrc);
      return;
    }

    if (behavior === 'filter_by_alt') {
      const tokens = variantOptions(variant);
      const next = state.allMedia.filter((item) => {
        const alt = normalizeToken(item.alt);
        return tokens.some((token) => token && alt.includes(token));
      });
      state.setVisible(next, variantSrc);
      return;
    }

    if (behavior === 'filter_by_filename') {
      const tokens = variantOptions(variant);
      const next = state.allMedia.filter((item) => {
        const filename = normalizeToken(item.filename);
        return tokens.some((token) => token && filename.includes(token));
      });
      state.setVisible(next, variantSrc);
      return;
    }
  }

  function getCurrentVariantId(config) {
    const fromInput = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]')?.value;
    if (fromInput) return String(fromInput);

    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return String(fromUrl);

    return config?.selectedVariantId ? String(config.selectedVariantId) : null;
  }

  function findVariant(product, variantId) {
    if (!variantId || !Array.isArray(product?.variants)) return null;
    return product.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  function findVariantForMedia(product, media) {
    if (!media || media.mediaType !== 'image' || !Array.isArray(product?.variants)) return null;

    if (media.variantIds.length) {
      const direct = product.variants.find((variant) => media.variantIds.includes(String(variant.id)));
      if (direct) return direct;
    }

    return product.variants.find((variant) => {
      const imageId = variantImageId(variant);
      const imageSrc = variantImageSrc(variant);
      return (
        (imageId && String(imageId) === String(media.id)) ||
        (imageSrc && sameImage(imageSrc, media.src)) ||
        (imageSrc && sameImage(imageSrc, media.previewSrc))
      );
    }) || null;
  }

  function findMediaForVariant(media, variant) {
    const variantId = variant?.id ? String(variant.id) : '';
    const imageId = variantImageId(variant);
    const imageSrc = variantImageSrc(variant);

    return media.find((item) => {
      return (
        (variantId && item.variantIds.includes(variantId)) ||
        (imageId && String(imageId) === String(item.id)) ||
        (imageSrc && sameImage(item.src, imageSrc)) ||
        (imageSrc && sameImage(item.previewSrc, imageSrc))
      );
    }) || null;
  }

  function variantImageId(variant) {
    return (
      variant?.featured_image?.id ||
      variant?.featured_media?.id ||
      variant?.image?.id ||
      variant?.image_id ||
      ''
    );
  }

  function variantImageSrc(variant) {
    return normalizeSrc(
      variant?.featured_image?.src ||
        variant?.featured_media?.preview_image?.src ||
        variant?.featured_media?.src ||
        variant?.image?.src ||
        ''
    );
  }

  function findMediaIndex(media, src) {
    if (!src) return -1;
    return media.findIndex((item) => sameImage(item.src, src));
  }

  function sameImage(a, b) {
    return stripImageSize(normalizeSrc(a)) === stripImageSize(normalizeSrc(b));
  }

  function normalizeSrc(src) {
    if (!src) return '';
    if (src.startsWith('//')) return `${window.location.protocol}${src}`;
    return src;
  }

  function stripImageSize(src) {
    return String(src)
      .replace(/_(\d+x\d+|pico|icon|thumb|small|compact|medium|large|grande|master)(?=\.)/i, '')
      .replace(/\?.*$/, '');
  }

  function srcsetFor(src, widths) {
    return widths
      .map((w) => `${sizedImage(src, w)} ${w}w`)
      .join(', ');
  }

  function imgDimensions(item) {
    if (!item.aspectRatio) return {};
    const m = String(item.aspectRatio).match(/^([\d.]+)\s*\/\s*([\d.]+)$/);
    if (!m) return {};
    return { width: Math.round(parseFloat(m[1])), height: Math.round(parseFloat(m[2])) };
  }

  function sizedImage(src, width) {
    const normalized = normalizeSrc(src);
    if (!normalized.includes('/cdn/shop/')) return normalized;
    return normalized.replace(/(\.(?:jpg|jpeg|png|webp|gif))(?:\?.*)?$/i, `_${width}x$1`);
  }

  function filenameFromSrc(src) {
    try {
      const url = new URL(normalizeSrc(src), window.location.origin);
      return decodeURIComponent(url.pathname.split('/').pop() || '');
    } catch {
      return String(src).split('/').pop() || '';
    }
  }

  function variantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3, variant.title]
      .filter(Boolean)
      .map((value) => normalizeToken(value));
  }

  function normalizeToken(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function patchHistory() {
    ['pushState', 'replaceState'].forEach((method) => {
      const original = history[method];
      if (original.__pbGalleryPatched) return;
      history[method] = function patchedHistoryMethod() {
        const result = original.apply(this, arguments);
        setTimeout(() => handlePossibleVariantChange(), 0);
        return result;
      };
      history[method].__pbGalleryPatched = true;
    });
  }

  function cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/"/g, '\\"');
  }
})();
